import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';

const CONTENT_ROUTES = ['/role-plays']; // Prompt Studio is admin-only; trainees reach module/scenario notifications via /role-plays
// Heartbeat while the tab is in front. Without it the unread badge only refreshed
// on login, on returning to the tab, or on hitting a content route — so a review
// request / assignment / completion arriving while the user sat on a page left the
// bell with no red badge until they navigated or reloaded.
const HEARTBEAT_MS = 60_000;
// Staleness threshold for the heartbeat, kept below its own period. `lastPolledAt`
// is stamped when the request *completes*, so reusing HEARTBEAT_MS here made every
// other tick bail out (`120000 - (60000 + rtt) < 60000`) and the badge landed at
// ~180s instead of ~120s. The headroom absorbs the round trip.
const HEARTBEAT_STALE_MS = HEARTBEAT_MS - 5_000;
const TAB_FOCUS_THRESHOLD_MS = 30_000;         // returning to the tab: refresh unless just polled
const CONTENT_ROUTE_THRESHOLD_MS = 10 * 60_000; // 10 min — catches New Teaching Module / New Scenario

export function useNotificationPolling(): void {
    const { pollIfStale } = useNotifications();
    const location = useLocation();

    // Heartbeat — only while visible, so background tabs cost nothing.
    useEffect(() => {
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') pollIfStale(HEARTBEAT_STALE_MS);
        }, HEARTBEAT_MS);
        return () => clearInterval(id);
    }, [pollIfStale]);

    // Tab visibility trigger — poll when the user returns to this tab after 5+ min away
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                pollIfStale(TAB_FOCUS_THRESHOLD_MS);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [pollIfStale]);

    // Route-based trigger — poll when navigating to content browsing pages (if not polled recently)
    useEffect(() => {
        const isContentRoute = CONTENT_ROUTES.some((r) => location.pathname.startsWith(r));
        if (isContentRoute) {
            pollIfStale(CONTENT_ROUTE_THRESHOLD_MS);
        }
    }, [location.pathname, pollIfStale]);
}
